import { useState } from "react";
import type { Notebook, Task } from "@/kernel/runner";
import { highlightLine, type Token } from "@/ui/highlight";
import { count } from "@/lib/plural";
import { Markdown } from "../notebook/markdown";
import { promptFor } from "../notebook/TaskView";
import { cellLabel, traceLines, type Failure } from "./model";

// Подсветка как в блоках кода (A0); строки с Error/Traceback — --err 600.
const tokenClass: Record<Token["kind"], string> = {
  comment: "italic text-mut",
  string: "text-warn",
  keyword: "text-acc",
  number: "text-info",
  func: "font-semibold text-ink",
  plain: "",
};

export function TraceLine({ line }: { line: string }) {
  if (/Error|Traceback/.test(line))
    return (
      <div>
        <span style={{ color: "var(--err)", fontWeight: 600 }}>{line}</span>
      </div>
    );
  if (line === "") return <div>{" "}</div>;
  return (
    <div>
      {highlightLine(line).map((t, j) => (
        <span key={j} className={tokenClass[t.kind]}>
          {t.text}
        </span>
      ))}
    </div>
  );
}

function FailureItem({
  f,
  task,
  nb,
  open,
  onToggle,
  onOpenCell,
}: {
  f: Failure;
  task?: Task;
  nb?: Notebook;
  open: boolean;
  onToggle: () => void;
  onOpenCell: () => void;
}) {
  const [prompt, setPrompt] = useState(false);
  const label = cellLabel(nb, task, f.cellId);
  const text = nb && task ? promptFor(nb, task) : "";
  return (
    <div data-testid="failure" data-task={f.n} className="overflow-hidden rounded-md border border-bd bg-card" style={{ borderLeft: "2px solid var(--err)" }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-3.5 py-3 text-left"
        style={{ color: "var(--ink)" }}
      >
        <span className="font-mono text-[12px]" style={{ color: "var(--err)" }}>
          ✕
        </span>
        <span className="text-[13.5px] font-semibold">
          Задача {f.n}
          {f.title ? ` · ${f.title}` : ""}
        </span>
        <span className="font-mono text-[11.5px]" style={{ color: "var(--err)" }}>
          {f.kind}
        </span>
        <span className="flex-1" />
        {label && <span className="font-mono text-[11px] text-mut">{label}</span>}
        <span aria-hidden className="font-mono text-[13px] text-mut">
          {open ? "−" : "+"}
        </span>
      </button>
      {open && (
        <div className="border-t border-bd bg-bg2">
          <pre data-testid="traceback" className="m-0 overflow-x-auto whitespace-pre-wrap px-[15px] py-[13px] font-mono text-[12.5px] leading-[1.65] text-ink">
            {traceLines(f).map((l, i) => (
              <TraceLine key={i} line={l} />
            ))}
          </pre>
          {prompt && (
            <div data-testid="task-prompt" className="border-t border-bd bg-card px-3.5 py-3">
              {text ? <Markdown text={text} /> : <p className="m-0 text-[13px] text-ink2">Условие задачи не найдено в ноутбуке — открой ячейку, оно над ней.</p>}
            </div>
          )}
          <div className="flex gap-2.5 border-t border-bd px-3.5 py-3">
            <button
              type="button"
              onClick={onOpenCell}
              className="cursor-pointer rounded-sm border border-bd2 bg-transparent px-3 py-[7px] font-mono text-[12px] hover:border-acc"
              style={{ color: "var(--ink)" }}
            >
              открыть ячейку
            </button>
            <button
              type="button"
              onClick={() => setPrompt((v) => !v)}
              aria-expanded={prompt}
              className="cursor-pointer rounded-sm border border-bd2 bg-transparent px-3 py-[7px] font-mono text-[12px] hover:border-acc"
              style={{ color: "var(--ink2)" }}
            >
              {prompt ? "скрыть условие" : "показать условие"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** «Упавшие проверки»: первая раскрыта; раскрытие управляется снаружи, чтобы
 *  сетка подсвечивала последнюю раскрытую задачу. */
export function Failures({
  items,
  tasks,
  nb,
  open,
  onToggle,
  onOpenCell,
}: {
  items: Failure[];
  tasks: Task[];
  nb?: Notebook;
  open: Record<number, boolean>;
  onToggle: (n: number) => void;
  onOpenCell: (n: number) => void;
}) {
  if (items.length === 0) return null;
  return (
    <section>
      <div className="mb-3 flex items-baseline gap-2.5">
        <h2 className="m-0 font-mono text-[14px] font-semibold">Упавшие проверки</h2>
        <span className="font-mono text-[11.5px]" style={{ color: "var(--err)" }} data-testid="failures-count">
          {count(items.length, "задача", "задачи", "задач")}
        </span>
      </div>
      <div className="flex flex-col gap-2.5">
        {items.map((f) => (
          <FailureItem key={f.n} f={f} task={tasks.find((t) => t.n === f.n)} nb={nb} open={!!open[f.n]} onToggle={() => onToggle(f.n)} onOpenCell={() => onOpenCell(f.n)} />
        ))}
      </div>
    </section>
  );
}
